/**
 * Block index utilities for finding block IDs and calculating offsets
 */

import {
  isValidBlockIdString,
  parseBlockId,
} from '../../../../utils/BlockUtils';
import { BlockIndex } from '../types';

/**
 * Gets the block ID from an element
 */
export function getBlockId(element: Element): BlockIndex | null {
  const id = element.id;

  if (isValidBlockIdString(id)) {
    return parseBlockId(id);
  }

  return null;
}

/**
 * Finds the closest block ID for a given node by walking up the DOM tree
 */
export function findClosestBlockId(node: Node): {
  blockId: BlockIndex | null;
  element: Node | null;
} {
  let currentElement: Node | null = node;

  while (currentElement) {
    if (currentElement.nodeType === Node.ELEMENT_NODE) {
      const element = currentElement as Element;
      const parsedId = getBlockId(element);

      if (parsedId !== null) {
        return { blockId: parsedId, element: currentElement };
      }
    }

    currentElement = currentElement.parentNode;
  }

  return { blockId: null, element: null };
}

/**
 * Finds block information for a node (current and parent block IDs)
 * This handles nested blocks where a child element might be inside a parent block
 */
export function findBlockInfo(node: Node): {
  id: BlockIndex | null;
  parentId: BlockIndex | null;
} {
  const { blockId, element: blockElement } = findClosestBlockId(node);

  if (blockId === null || blockElement === null) {
    return { id: null, parentId: null };
  }

  // Find the parent block ID by starting from the parent of the block element
  const parentNode = blockElement.parentNode;
  let parentId: BlockIndex | null = null;

  if (parentNode) {
    const result = findClosestBlockId(parentNode);
    parentId = result.blockId;
  }

  return { id: blockId, parentId };
}
